// Modules
import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'

// Application
import { useRecoverContext, useApp } from 'context'
import { useAccountService } from 'services/account'
import { ChangePasswordInfoInput, Contact, ContactType } from 'typings'
import { useRecoverService } from 'services/recoverpassword'
import { PatientToken, PatienTokenResponse, RecoverStep, Contacts, OTPTokenData, AuthenticateOtp } from 'typings/recoverpassword'
import { goToPage } from 'redesign/utils/goToPage'
import { useLoginHook, useRedirectTo } from 'hooks'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import { useAuthentication } from 'redesign/hooks/useAuthentication'
import { ROUTES_PATH, tracking } from 'constants/index'

export const useRecoverHook = () => {
    const { state, operations } = useRecoverContext()
    const { resendToken, resendTokenV2 } = useAccountService()
    const { openHelpModal } = useApp()
    const service = useRecoverService()
    const history = useHistory()
    const { redirectToPath } = useRedirectTo()
    const { handleAuthUserAfterChangePassword } = useAuthentication()
    const { verifyUser } = useLoginHook()

    const redirectTo = sessionStorage.getItem('@LiviaAuth:Campaigns')

    const getContactList = async (username: string): Promise<void> => {
        const data = await service.getContactList(username)
        operations.setEmail(data.email)
        operations.setPhone(data.mobile)
    }

    const setContacts = async (username: string, contacts: Contacts) => {
        operations.setUsername(username)
        operations.setContacts(contacts)
    }

    const messageChangePassword = async (type: ContactType): Promise<void> => {
        const username = state.username!
        await service.messageChangePassword(username, type)
    }

    const sendResetToken = async (contact: Contact, usernameLS?: string): Promise<void> => {
        const username = state.username!.replace(/[^a-z0-9]/gi, '')
        await service.sendResetToken(username ? username : usernameLS!, contact.type!, contact.id!)
    }

    const checkToken = async (patientToken: PatientToken): Promise<PatienTokenResponse> => {
        const data = await service.checkToken(patientToken)
        return data
    }

    const sendOTPToken = async (contact: Contact): Promise<OTPTokenData> => {
        const username = state.username!
        const data = await service.sendOTPToken(username, contact.type!, contact.id!)
        return data
    }

    const checkOTPToken = async (key: string, password: string, cpf: string): Promise<AuthenticateOtp> => {
        const data = await service.checkOTPToken(key, password, cpf)
        return data
    }

    const nextStep = async (data?: ChangePasswordInfoInput, type?: string): Promise<void> => {
        if (state.step !== RecoverStep.newPassword) {
            if (type === 'otp') {
                history.push(`${ROUTES_PATH.LOGIN_WITH_OTP}?step=${state.step + 1}`)
            } else {
                history.push(`${ROUTES_PATH.RECOVER_PASSWORD}?step=${state.step + 1}`)
            }
        } else {
            if (state.redirectTo) {
                goToPage(state.redirectTo)
            } else {
                operations.setSubmitting(true)
                const payload = {
                    cpf: String(data?.username),
                    password: String(data?.newPassword),
                }
                const passwordRecoverTrackValues = {
                    externalStep: 'RECOVER_PASSWORD_LOGIN',
                    loginType: tracking.LOGIN_TYPE.RESET,
                }

                await handleAuthUserAfterChangePassword(payload)
                await verifyUser(passwordRecoverTrackValues)
                redirectToPath({
                    defaultRedirect: redirectTo ?? defaultConfig.strings.AUTH_REDIRECT_LOGIN,
                })
            }

            operations.resetStep()
        }
    }

    const previousStep = (): void => {
        if (state.step !== RecoverStep.selectContact) {
            operations.previousStep()
        }
    }

    const setCurrentStep = useCallback((step: RecoverStep) => operations.setStep(step), [operations])

    const moveToError = (path: string, historyState?: unknown): void => {
        history.push(path, historyState)
    }

    const setOtpKey = (OtpKey: string): void => {
        operations.setOtpKey(OtpKey)
    }

    return {
        ...state,
        nextStep,
        checkToken,
        setContacts,
        moveToError,
        resendToken,
        sendOTPToken,
        checkOTPToken,
        resendTokenV2,
        previousStep,
        openHelpModal,
        sendResetToken,
        getContactList,
        setCurrentStep,
        messageChangePassword,
        changePassword: service.changePassword,
        setRedirectTo: operations.setRedirectTo,
        setContact: operations.setContact,
        setUsername: operations.setUsername,
        setSubmitting: operations.setSubmitting,
        setValidateToken: operations.setValidateToken,
        resetRecoverPasswordState: operations.resetRecoverPasswordState,
        setOtpKey,
    }
}
